import {TileSelectionManager} from '@/map/tiles/TileSelectionManager';
import {HighlightManager} from '@/map/HighlightManager';
import {RegionBorderRenderer} from '@/map/RegionBorderRenderer';
import {edgesToStr, FeatureType, GameStateDTO, HexEdge} from "@hexcrawl/shared-utils";
import { useTooltipStore } from '@/ui/stores/tooltipStore';
import { useDiscoveryStore } from '@/ui/stores/discoveryStore';

import Camera = Phaser.Cameras.Scene2D.Camera;
import MapData = Phaser.Tilemaps.MapData;
import Orientation = Phaser.Tilemaps.Orientation;
import Scene = Phaser.Scene;
import Sprite = Phaser.GameObjects.Sprite;
import Tilemap = Phaser.Tilemaps.Tilemap;
import TilemapLayer = Phaser.Tilemaps.TilemapLayer;
import Tileset = Phaser.Tilemaps.Tileset;
import Vector2 = Phaser.Math.Vector2;
import {getSpriteAssetKey} from '@/utils/sprites.ts';

const TILE_WIDTH = 512;
const TILE_HEIGHT = 591;
const TILE_TOP_OVERLAP = 262; // extra space on the top of tiles for elements that poke over the next hex above them (ie mountain tops, trees, etc.)
const TILE_HEIGHT_TOTAL = TILE_HEIGHT + TILE_TOP_OVERLAP;
const HEX_SIDE_LENGTH = 294;

const MIN_ZOOM = 0.01;
const MAX_ZOOM = 0.5;

export class MapRenderer {
  private _scene: Scene;
  private _gameState: GameStateDTO;

  private _tilemap?: Tilemap;
  private _tileSelectionManager: TileSelectionManager = new TileSelectionManager();
  private _highlightManager: HighlightManager = new HighlightManager(TILE_WIDTH, TILE_HEIGHT, HEX_SIDE_LENGTH);
  private _regionBorderRenderer: RegionBorderRenderer = new RegionBorderRenderer();

  private _mapWorldWidth = 0;
  private _mapWorldHeight = 0;
  private _minZoom = 0;
  private _maxZoom = 0;


  constructor(scene: Scene, gameState: GameStateDTO) {
    this._scene = scene;
    this._gameState = gameState;
  }

  get scene(): Scene { return this._scene; }
  get worldWidth(): number { return this._mapWorldWidth; }
  get worldHeight(): number { return this._mapWorldHeight; }
  get minZoom(): number { return this._minZoom; }
  get maxZoom(): number { return this._maxZoom; }

  // Returns the center point of the hex at hex coordinate (x, y) in world coordinates
  hexToWorld(x: number, y: number): Vector2 | undefined {
    const worldCoords = this._tilemap?.tileToWorldXY(Math.trunc(x), Math.trunc(y));
    if (!worldCoords) return undefined;
    worldCoords.y += TILE_TOP_OVERLAP;
    return worldCoords;
  }

  // Returns the hex coordinates of the hex that contains the world position (x, y)
  worldToHex(x: number, y: number): Vector2 | undefined {
    y -= TILE_TOP_OVERLAP;
    return this._tilemap?.worldToTileXY(x, y) || undefined;
  }

  centerCameraOnHex(camera: Camera, x: number, y: number) {
    // Convert tile coordinates to world coordinates (top-left of the tile area)
    const worldPos = this.hexToWorld(x, y);
    if (!worldPos) return;
    camera.centerOn(worldPos.x, worldPos.y);
  }

  setZoom(camera: Camera, zoom: number) {
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
    camera.setZoom(newZoom);
  }

  cleearAllHighlight() {
    this._highlightManager.clear();
  }

  setHighlight(x: number, y: number, isHighlight: boolean) {
    this._highlightManager.setHighlight(this, x, y, isHighlight);
  }

  createSprite(spriteRef: string, x: number, y: number): Sprite | undefined {
    if (!this._tilemap) return undefined;

    const textureKey = getSpriteAssetKey(spriteRef);
    if (!this._scene.textures.exists(textureKey)) return undefined;

    const worldPos = this.hexToWorld(x, y);
    if (!worldPos) return undefined;

    let sprite: Sprite;
    sprite = this._scene.add.sprite(worldPos.x, worldPos.y, textureKey);
    sprite.setScale(0.5);
    sprite.setDepth(10000);

    return sprite;
  }

  private _discoveryMarkerSprites: Phaser.GameObjects.Sprite[] = [];
  private _questMarkerSprites: Phaser.GameObjects.Sprite[] = [];

  private static readonly DISCOVERY_MARKER_FRAME = 'Icon_Large_Star_SeethroughOutline.png';
  private static readonly DISCOVERY_MARKER_SCALE = 0.4;
  // load.atlas() does not apply the "rotated: true" atlas flag, so compensate manually
  public static readonly QUEST_LOCATION_FRAME = 'Icon_Small_Coin.png';
  public static readonly QUEST_RETURN_FRAME = 'Icon_Small_Coin.png';
  private static readonly QUEST_MARKER_SCALE = 0.6;

  renderDiscoveryMarkers(markers: Array<{id: string; x: number; y: number; name: string}>) {
    if (!this._tilemap) return;

    for (const sprite of this._discoveryMarkerSprites) {
      sprite.destroy();
    }
    this._discoveryMarkerSprites = [];

    const xOffset = this._tilemap.tileWidth * 0.2;
    const yOffset = this._tilemap.tileHeight * 0.25;

    for (const marker of markers) {
      const worldPos = this.hexToWorld(marker.x, marker.y);
      if (!worldPos) continue;

      const sprite = this._scene.add.sprite(
        worldPos.x + xOffset,
        worldPos.y - yOffset,
        'icons',
        MapRenderer.DISCOVERY_MARKER_FRAME
      );
      sprite.setOrigin(0.5, 0.5);
      sprite.setScale(MapRenderer.DISCOVERY_MARKER_SCALE);
      sprite.setDepth(9000);
      sprite.setInteractive();

      const { id, name } = marker;
      sprite.on('pointerover', () => useTooltipStore.getState().show(name));
      sprite.on('pointerout', () => useTooltipStore.getState().hide());
      sprite.on('pointerdown', () => useDiscoveryStore.getState().openLoreAtId(id));

      this._discoveryMarkerSprites.push(sprite);
    }
  }

  renderQuestMarkers(markers: Array<{x: number; y: number; title: string; frame: string;}>) {
    if (!this._tilemap) return;

    for (const sprite of this._questMarkerSprites) {
      sprite.destroy();
    }
    this._questMarkerSprites = [];

    const xOffset = this._tilemap.tileWidth * -0.2;
    const yOffset = this._tilemap.tileHeight * 0.25;

    for (const marker of markers) {
      const worldPos = this.hexToWorld(marker.x, marker.y);
      if (!worldPos) continue;

      const sprite = this._scene.add.sprite(
        worldPos.x + xOffset,
        worldPos.y - yOffset,
        'icons',
        marker.frame
      );
      sprite.setOrigin(0.5, 0.5);
      sprite.setScale(MapRenderer.QUEST_MARKER_SCALE);
      sprite.setDepth(9001);
      sprite.setInteractive();

      const title = marker.title;
      sprite.on('pointerover', () => useTooltipStore.getState().show(title));
      sprite.on('pointerout', () => useTooltipStore.getState().hide());

      this._questMarkerSprites.push(sprite);
    }
  }

  renderRegionBorders(regions: Array<Array<{ x: number; y: number }>>) {
    this._regionBorderRenderer.render(this, regions);
  }

  async loadTilesetData() {
    // Load tileset JSON files and parse them
    const terrainTilesetUrls = [
      '/assets/tilesets/hex_terrain.tsj',
      '/assets/tilesets/hex_terrain_forest_rivers.tsj',
      '/assets/tilesets/hex_terrain_forest_roads.tsj',
      '/assets/tilesets/hex_terrain_forest_bridges.tsj',
      '/assets/tilesets/hex_terrain_plains_rivers.tsj',
      '/assets/tilesets/hex_terrain_plains_roads.tsj',
      '/assets/tilesets/hex_terrain_plains_bridges.tsj'
    ];
    const featureTilesetUrls = [
      '/assets/tilesets/hex_features.tsj'
    ];

    const allTileSets = await this._tileSelectionManager.loadTileSets(terrainTilesetUrls, featureTilesetUrls);

    // Load all individual tileset images
    for (const tileSet of allTileSets) {
      const key = `tileset-${tileSet.name}`;
      // Image paths in tileset JSON are relative to the JSON file location
      console.log(`Loaded tile image ${key} from ${tileSet.image}`);
      const imagePath = `/assets/tilesets/${tileSet.image}`;

      if (!this._scene.textures.exists(key)) {
        this._scene.load.image(key, imagePath);
      }
    }
  }

  onDataLoaded() {
    if (!this._gameState.map) {
      throw new Error('Map data not loaded yet');
    }

    const mapData: MapData = new MapData({
      name: `map-${this._gameState.id}`,
      width: this._gameState.map.width,
      height: this._gameState.map.height,
      tileWidth: TILE_WIDTH,
      tileHeight: TILE_HEIGHT,
      orientation: Orientation.HEXAGONAL,
      renderOrder: 'right-down'
    });

    mapData.staggerIndex = 'odd';
    mapData.staggerAxis = 'y';
    mapData.hexSideLength = HEX_SIDE_LENGTH;

    this._tilemap = new Tilemap(this._scene, mapData);
    this.populateAllTiles();
    this.calculateBounds();
    this.calculateZoomRange();
  }

  private calculateBounds() {
    if (!this._gameState.map || !this._tilemap) {
      throw new Error('Map data not loaded.');
    }

    const mapWorldSize = this._tilemap.tileToWorldXY(this._gameState.map.width, this._gameState.map.height);
    if (!mapWorldSize) return;
    this._mapWorldWidth = mapWorldSize.x;
    this._mapWorldHeight = mapWorldSize.y + TILE_TOP_OVERLAP;
  }

  private calculateZoomRange() {
    // Determine zoom range
    const screenWidth = this._scene.cameras.main.width;
    const screenHeight = this._scene.cameras.main.height;
    const zoomX = screenWidth / this.worldWidth;
    const zoomY = screenHeight / this.worldHeight;
    this._minZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.min(zoomX, zoomY)));
    this._maxZoom = MAX_ZOOM;
  }

  private populateAllTiles() {
    if (!this._gameState.map || !this._tilemap) {
      throw new Error('Map data not loaded.');
    }

    const tilesetDatas = this._tileSelectionManager.getAllTileSets();

    // Add tilesets to the _tilemap
    // Phaser does not natively support the "collection of images" tileset model, which we're currently using.
    // Our silly work-around here is to create one tileset per tile, which is a bit much but works for now.
    // We may eventually want to render out a single image per tileset for the sake of performance.
    const tilesets: Tileset[] = [];
    for (const tilesetData of tilesetDatas) {
      const key = `tileset-${tilesetData.name}`;
      const tileset = this._tilemap.addTilesetImage(key, key, TILE_WIDTH, TILE_HEIGHT_TOTAL, 0, 0, tilesetData.startingId);
      if (tileset) {
        tilesets.push(tileset);
      }
    }

    // Create layers in order: terrain → features
    const terrainLayer = this._tilemap.createBlankLayer('terrain', tilesets);
    const featuresLayer = this._tilemap.createBlankLayer('features', tilesets);

    if (!terrainLayer || !featuresLayer) {
      throw new Error('Failed to create _tilemap layers');
    }

    this.populateTerrain(terrainLayer);
    this.populateFeatures(terrainLayer, featuresLayer);
  }

  private populateTerrain(terrainLayer: TilemapLayer) {
    if (!this._gameState.map || !this._tilemap) {
      throw new Error('Map data not loaded.');
    }

    // Populate terrain layer
    for (let y = 0; y < this._gameState.map.height; y++) {
      for (let x = 0; x < this._gameState.map.width; x++) {
        const idx = y * this._gameState.map.width + x;
        const hex = this._gameState.map.hexes[idx];
        const rivers: HexEdge = hex.rivers;
        const roads: HexEdge = hex.roads;

        // console.log(`Populating terrain tile at (${x}, ${y}) [terrain: ${hex.terrain}, rivers: ${rivers}, roads: ${roads}]`);

        if (hex?.terrain) {
          const gid = this._tileSelectionManager.selectTerrainTile(
            hex.terrain,
            x,
            y,
            rivers,
            roads,
            this._gameState.map.seed
          );

          if (gid !== null && gid !== 0) {
            terrainLayer.putTileAt(gid, x, y);
          } else {
            console.warn(`No terrain tile found for terrain ${hex.terrain} at (${x}, ${y}) (rivers: [${edgesToStr(rivers)}], roads: [${edgesToStr(roads)}])`);
          }
        }
      }
    }
  }

  private populateFeatures(terrainLayer: TilemapLayer, featuresLayer: TilemapLayer) {
    if (!this._gameState.map || !this._tilemap) {
      throw new Error('Map data not loaded.');
    }

    for (let y = 0; y < this._gameState.map.height; y++) {
      for (let x = 0; x < this._gameState.map.width; x++) {
        const idx = y * this._gameState.map.width + x;
        const hex = this._gameState.map.hexes[idx];
        if (hex.feature == FeatureType.NONE) continue;

        const {gid, isTerrainSpecific} = this._tileSelectionManager.selectFeatureTile(
          hex.feature,
          hex.terrain,
          hex.x,
          hex.y,
          this._gameState.map.seed
        );

        if (gid !== null && gid !== 0) {
          // Terrain-specific features go on terrain layer, generic features on features layer
          const targetLayer = isTerrainSpecific ? terrainLayer : featuresLayer;
          targetLayer.putTileAt(gid, hex.x, hex.y);
        } else {
          console.warn(`No feature tile found for feature ${hex.feature} at (${hex.x}, ${hex.y})`);
        }
      }
    }
  }
}
